import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOnboarding } from './OnboardingContext';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { 
  CheckCircle2, Circle, ChevronDown, ChevronRight,
  X, ListChecks, Sparkles, ExternalLink
} from 'lucide-react';

const OnboardingChecklist = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    showChecklist, setShowChecklist, workflow, progressPercentage,
    onboardingData, isSubstepCompleted, isStepCompleted, getStepProgress,
    completeSubstep, skipOnboarding
  } = useOnboarding();

  const [expandedSteps, setExpandedSteps] = useState({});
  const [isExpanded, setIsExpanded] = useState(false);

  const stepTitleKeys = {
    'Complete Your Profile': 'onboarding.complete_your_profile',
    'Explore Your Dashboard': 'onboarding.explore_dashboard',
    'Upload Your Documents': 'onboarding.upload_documents',
    'Manage Your Ledger': 'onboarding.manage_ledger',
    'Connect Your Bank (Optional)': 'onboarding.connect_bank',
    'Maximize Deductions': 'onboarding.maximize_deductions',
    'Plan Quarterly Payments': 'onboarding.plan_quarterly',
    'Generate Reports': 'onboarding.generate_reports',
    'Get AI Tax Advice': 'onboarding.get_ai_advice'
  };

  const getStepTitle = (title) => {
    const key = stepTitleKeys[title];
    return key ? t(key, title) : title;
  };

  if (onboardingData?.progress?.never_show_again || !workflow.length) return null;

  const toggleStep = (stepId) => {
    setExpandedSteps(prev => ({ ...prev, [stepId]: !prev[stepId] }));
  };

  const handleSubstepClick = async (step) => { navigate(step.page); };

  const handleMarkComplete = async (e, stepId, substepId) => {
    e.stopPropagation();
    await completeSubstep(stepId, substepId);
  };

  const handleNeverShow = () => {
    if (window.confirm('Are you sure? You can always restart the guide from Settings.')) {
      skipOnboarding(true);
    }
  };

  const completedSteps = workflow.filter(s => isStepCompleted(s.id)).length;

  // Minimized state - floating pill button
  if (!showChecklist || !isExpanded) {
    return (
      <div className="fixed bottom-5 right-5 z-50" data-testid="onboarding-bubble">
        <button
          onClick={() => { setShowChecklist(true); setIsExpanded(true); }}
          className="group flex items-center gap-2.5 bg-emerald-600 hover:bg-emerald-700 text-white pl-4 pr-5 py-2.5 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
          aria-label={`Getting started: ${progressPercentage}% complete`}
        >
          <div className="relative w-8 h-8">
            <svg className="w-8 h-8 -rotate-90" viewBox="0 0 36 36">
              <path className="text-emerald-800" stroke="currentColor" strokeWidth="3" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
              <path className="text-white" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray={`${progressPercentage}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold">{progressPercentage}%</span>
          </div>
          <span className="text-sm font-medium hidden sm:inline">Setup Guide</span>
        </button>
      </div>
    );
  }

  // Expanded floating widget
  return (
    <div className="fixed bottom-5 right-5 z-50 w-[340px] max-w-[calc(100vw-2.5rem)] animate-in slide-in-from-bottom-4 fade-in duration-200" data-testid="onboarding-checklist">
      <div className="bg-white rounded-2xl shadow-2xl border border-stone-200 overflow-hidden">
        {/* Header */}
        <div className="bg-emerald-600 px-5 py-4 text-white">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <h3 className="font-semibold text-sm">{t('onboarding.getting_started', 'Getting Started')}</h3>
            </div>
            <div className="flex items-center gap-0.5">
              <button onClick={() => setIsExpanded(false)} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors" title="Minimize">
                <ChevronDown className="w-4 h-4" />
              </button>
              <button onClick={handleNeverShow} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors" title="Don't show again">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-emerald-100">{completedSteps} of {workflow.length} steps</span>
              <span className="font-semibold">{progressPercentage}%</span>
            </div>
            <div className="h-1.5 bg-emerald-800/40 rounded-full overflow-hidden">
              <div className="h-full bg-white rounded-full transition-all duration-700 ease-out" style={{ width: `${progressPercentage}%` }} />
            </div>
          </div>
        </div>

        {/* Steps List */}
        <div className="max-h-[50vh] overflow-y-auto p-3 space-y-1">
          {workflow.map((step, index) => {
            const { completed, total } = getStepProgress(step.id);
            const stepDone = isStepCompleted(step.id);
            const isOpen = expandedSteps[step.id];

            return (
              <Collapsible key={step.id} open={isOpen} onOpenChange={() => toggleStep(step.id)}>
                <CollapsibleTrigger className="w-full">
                  <div className={`flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all text-left w-full ${
                    stepDone ? 'bg-emerald-50' : 'hover:bg-stone-50'
                  }`}>
                    {stepDone ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-stone-300 flex items-center justify-center flex-shrink-0">
                        <span className="text-[10px] font-bold text-stone-400">{index + 1}</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-medium truncate ${stepDone ? 'text-emerald-700' : 'text-stone-700'}`}>
                        {getStepTitle(step.title)}
                      </p>
                      {!stepDone && (
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <div className="h-1 flex-1 bg-stone-200 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }} />
                          </div>
                          <span className="text-[10px] text-stone-400">{completed}/{total}</span>
                        </div>
                      )}
                    </div>
                    {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" />}
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="pl-10 pr-3 pb-2 space-y-0.5">
                    {step.substeps?.map((substep) => {
                      const subDone = isSubstepCompleted(step.id, substep.id);
                      return (
                        <div key={substep.id}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors ${
                            subDone ? 'text-emerald-600' : 'text-stone-500 hover:bg-stone-50 hover:text-stone-700'
                          }`}
                          onClick={() => handleSubstepClick(step)}>
                          {subDone ? (
                            <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                          ) : (
                            <button onClick={(e) => handleMarkComplete(e, step.id, substep.id)} className="hover:text-emerald-500">
                              <Circle className="w-3.5 h-3.5 flex-shrink-0" />
                            </button>
                          )}
                          <span className="text-xs">{substep.title}</span>
                          {!subDone && (
                            <ExternalLink className="w-3 h-3 ml-auto text-stone-300 flex-shrink-0" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-stone-100 bg-stone-50/50">
          <button onClick={handleNeverShow} className="text-[11px] text-stone-400 hover:text-stone-600 transition-colors">
            Don't show again
          </button>
        </div>
      </div>
    </div>
  );
};

export { OnboardingChecklist };
export default OnboardingChecklist;
